<template>
  <div class="flex max-w-7xl w-full mx-auto pt-8 px-4">
    <div class="flex-1">
      <RecentPagePdfButton v-if="enableDownloadPDFButton" class="float-right mt-10 ui-font"  :page-id="'department-' + department.id" />
      <div class="px-4 max-w-xl mx-auto sm:px-6 lg:py-8 lg:max-w-3xl lg:mx-0 lg:px-0">
        <div class="theme-page-content mt-6 mr-4">
          <h1 class="text-5xl font-bold tracking-tight text-gray-900">
            {{ department.displayName }}
          </h1>
          <p
            v-if="department.catalogDescription && enableCatalogDescriptions"
            class="mt-4 text-lg text-gray-500"
            data-test="curriculumDepartmentCatalogDescription"
          >
            {{ department.catalogDescription }}
          </p>
        </div>
      </div>
      <ul
        class="flex border-b pl-4"
        role="tablist"
        data-test="dept-tab-list"
      >
        <li
          v-for="tab of tabs"
          :key="tab.key"
          :aria-selected="activeTab.key === tab.key ? 'true' : 'false'"
          :aria-controls="`panel-${tab.key}`"
          role="tab"
          class="-mb-px mr-1"
          @click="goToTab(tab.key)"
        >
          <div
            class="bg-white inline-block py-2 px-2 md:px-4 text-blue-600 font-semibold ui-font cursor-pointer"
            :class="{ 'tab-active': activeTab.key === tab.key }"
          >
            {{ tab.label }}
          </div>
        </li>
      </ul>
      <div
        :id="`panel-${activeTab.key}`"
        class="pl-4 pb-8 pt-2"
        role="tabpanel"
        data-test="tab-content"
      >
        <nuxt-child
          keep-alive
          :page="page"
          :department="department"
          :programs="programs"
          :courses="courses"
          :available-columns="availableColumns"
        />
      </div>
    </div>
    <slot name="sidebar" />
  </div>
</template>

<script>
import useFeatureFlags from '@/composables/useFeatureFlags'
import RecentPagePdfButton from '@/components/RecentPagePdfButton.vue'
import {
  DEPARTMENT_CATALOG_DESCRIPTIONS,
  DEPARTMENT_PAGE_PDF,
} from '@/constants/featureFlags'

export default {
  setup() {
    const {
      enableCatalogDescriptions,
      enableDownloadPDFButton,
    } = useFeatureFlags({
      enableCatalogDescriptions: DEPARTMENT_CATALOG_DESCRIPTIONS,
      enableDownloadPDFButton: DEPARTMENT_PAGE_PDF,
    })
    return { enableCatalogDescriptions, enableDownloadPDFButton } // now we can use enableCatalogDescriptions for v-if to enable or disable in template
  },
  components: { RecentPagePdfButton },
  props: {
    initialTab: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
    department: {
      type: Object,
      required: true,
    },
    programs: {
      type: Array,
      required: true,
    },
    courses: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeTabKey: null,
    }
  },
  computed: {
    activeTab() {
      const tabToSearchFor = this.activeTabKey || this.initialTab
      return this.tabs.find((t) => t.key === tabToSearchFor) || this.tabs[0]
    },
    availableColumns() {
      return this.activeTab.options?.filter((option) => option.enabled)
    },
  },
  methods: {
    async goToTab(tab) {
      const currentTab = this.activeTab.key
      if (currentTab === tab) return

      this.activeTabKey = tab

      const path = this.$route.path.replace(currentTab, this.activeTab.key)
      await this.$router.replace(path)
    },
  },
}
</script>

<style lang="css" scoped>
.tab-active {
  @apply border-l border-t border-r rounded-t;
}
.ui-font,
.ui-font:hover,
.ui-font:focus {
  font-weight: var(--interfaceFontWeight, inherit);
  color: var(--interfaceFontColor, inherit);
}
</style>
